import React, { useEffect, useState, useRef, useCallback } from 'react';

import Popout from 'components/popout';

import IconArrowLeft from 'icons/icon-arrow-left';
import IconArrowRight from 'icons/icon-arrow-right';
import IconHistory from 'icons/icon-history';

import './AppNav.styles.scss';

const AppHistoryItem = ({ locationKey, title, stack, currentKey, onClick }) => {
    const handleClick = () => {
        let count = false;
        let stepsBack = 0;

        stack.forEach(({ location }) => {
            if (location.key === locationKey) {
                count = true;
            }

            if (location.key === currentKey) {
                count = false;
            }

            if (count) {
                stepsBack += 1;
            }
        })

        onClick(stepsBack);
    }

    return (
        <li className="pair-item" onClick={handleClick}>
            <span className="key">
                { title }
            </span>
        </li>
    );
}

const AppNav = ({ history }) => {
    const popoutCtrl = useRef(null);
    const [stack, setStack] = useState([]);
    const [popoutVisible, setPopoutVisible] = useState(false);
    const [currentKey, setCurrentKey] = useState(null);
    const lastKeyRef = useRef();

    const createStackEntry = (location, getTitleDelay = 300) => {
        return new Promise(resolve => {
            const entry = {
                location,
                title: null,
                timestamp: Date.now()
            };

            setTimeout(() => {
                if (document.title) {
                    entry.title = document.title.replace(/\s\|\sNifty$/, '');
                }

                resolve(entry);
            }, getTitleDelay);
        });
    }

    const pushEntry = async (location, getTitleDelay) => {
        const entry = await createStackEntry(location, getTitleDelay);

        setStack(prevStack => {
            if (lastKeyRef.current) {
                prevStack = prevStack.slice(0, prevStack.findIndex(entry => entry.location.key === lastKeyRef.current) + 1);
            }

            return [...prevStack, entry]
        });
    }

    const handleGoBackClick = useCallback(() => {
        history.goBack();
    }, []);

    const handleGoForwardClick = useCallback(() => {
        history.goForward();
    }, []);

    const handleHistoryOpenClick = useCallback(() => {
        if (!popoutCtrl.current) {
            return;
        }

        if (popoutVisible) {
            popoutCtrl.current.hide();
        } else {
            popoutCtrl.current.show();
        }
    }, [popoutVisible]);

    const handleHistoryPopoutShow = useCallback(() => {
        setPopoutVisible(true);
    }, []);

    const handleHistoryPopoutHide = useCallback(() => {
        setPopoutVisible(false);
    }, []);

    const handleLocationChange = useCallback(async location => {
        if (location.key) {
            setTimeout(() => {
                setCurrentKey(location.key);
            }, 200);
        }

        if (location.action === 'PUSH') {
            await pushEntry(location);
        }

        if (location.action === 'REPLACE') {
            await pushEntry(location);
        }

        lastKeyRef.current = location.key;
    }, [currentKey]);

    const handleHistoryItemClick = useCallback(stepsBack => {
        history.go(-stepsBack);

        popoutCtrl.current && popoutCtrl.current.hide();
    }, []);

    useEffect(() => {
        const initialLocation = history.getCurrentLocation();
        pushEntry(initialLocation, 3000);
        setCurrentKey(initialLocation.key);

        history.listen(handleLocationChange);
    }, []);

    const fullHistoryStack = stack.slice(0, stack.findIndex(entry => entry.location.key === currentKey)).reverse();
    const historyStack = fullHistoryStack.slice(0, 5);

    return (
        <div className="app-nav">
            <div className="app-nav-arrows">
                <button className="btn btn-icon btn-square btn-small btn-solid" disabled={!fullHistoryStack.length} onClick={handleGoBackClick}>
                    <IconArrowLeft />
                </button>

                <button className="btn btn-icon btn-square btn-small btn-solid" disabled={!currentKey || !stack.length || stack[stack.length - 1]?.location.key === currentKey} onClick={handleGoForwardClick}>
                    <IconArrowRight />
                </button>
            </div>

            <div className="app-nav-history">
                <button className="btn btn-icon btn-square btn-small btn-transparent" onClick={handleHistoryOpenClick} disabled={!historyStack.length}>
                    <IconHistory />
                </button>

                <Popout
                    className="xxxsmall no-arrow"
                    xAlign="left"
                    defaultXPosition="right"
                    provideController={popoutCtrl}
                    onShow={handleHistoryPopoutShow}
                    onHide={handleHistoryPopoutHide}
                >
                    <div className="popout-main">
                        <ul className="popout-checklist widget-box-select-list">
                            {
                                historyStack.filter(({ title }) => Boolean(title)).map(({ location, title }) => <AppHistoryItem
                                    key={location.key}
                                    locationKey={location.key}
                                    title={title}
                                    currentKey={currentKey}
                                    stack={stack}
                                    onClick={handleHistoryItemClick}
                                />)
                            }
                        </ul>
                    </div>
                </Popout>
            </div>
        </div>
    )
}

export default AppNav;
